import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'

import {
  cancelComicEpisodeGenerateJob,
  type ComicEpisodeGenerateJob,
  listActiveComicEpisodeGenerateJobs,
} from '../api/comicEpisodeJobs'
import { getComicProject, isValidComicProjectPublicId } from '../api/comics'
import { isApiError } from '../api/errors'
import { DynamicIslandComicQueueDetailView } from '../components/theme/dynamic-island/detail/DynamicIslandComicQueueDetailView'
import { useDynamicIsland, useReportDynamicIslandActivity } from '../components/theme'
import { USER_PERMISSION, useUserPerm } from '../hooks/useUserPerm'
import { buildEpisodeGenerateJobPages } from '../lib/buildComicEpisodeGenerateJob'
import {
  attachEpisodeGenerateJob,
  buildQueueFromJob,
  runEpisodeGenerateJob,
} from '../lib/comicEpisodeGenerateJobRunner'
import { resolveEpisodePagesForGenerate } from '../lib/comicEpisodeGeneratePagePick'
import {
  createEpisodeGenerateQueue,
  type EpisodeGenerateQueueState,
  type EpisodeQueuePageStatus,
  formatEpisodeQueueCompletionToast,
} from '../lib/comicEpisodeGenerateQueue'
import {
  notifyComicEpisodeQueueActivity,
  subscribeComicEpisodeQueueActivity,
} from '../lib/comicEpisodeQueueBroadcast'
import { mapComicQueueToActivity } from '../lib/dynamicIsland/mapComicQueueActivity'
import { getEpisodeSettings } from '../lib/comicEpisodeSettings'
import { formatPagePanelCapacity, pagePanelCapacity } from '../lib/comicPageLayout'
import { pageHasGeneratableScript } from '../lib/comicPagePrompt'
import { getPanelsOnPage } from '../lib/comicPages'
import { getActiveEpisode } from '../lib/comicProjectMutations'
import { validateComicProjectSettings } from '../lib/comicProjectSettings'
import type { ComicProject } from '../lib/comicTypes'
import { useTheme } from '../theme/ThemeContext'
import { useAuthSession } from './AuthSessionContext'
import { usePlatformConfig } from './PlatformConfigContext'
import { useToast } from './ToastContext'

export type ComicEpisodeQueueEditorBridge = {
  projectId: string
  applyProject: (project: ComicProject, opts?: { save?: boolean }) => void
  persistProject: (project: ComicProject, quiet?: boolean) => Promise<void>
  waitForPageSelection: (pageId: string) => Promise<void>
  openPageCandidates: (pageId: string) => void
  scrollToPage?: (pageId: string) => void
}

type ComicEpisodeGenerateQueueContextValue = {
  queue: EpisodeGenerateQueueState | null
  generatingEpisodeId: string | null
  activeProjectId: string | null
  activeEpisodeId: string | null
  awaitingPageId: string | null
  isGeneratingEpisode: (episodeId: string) => boolean
  startEpisodeGenerate: (
    project: ComicProject,
    episodeId: string,
    options: { pageIds: string[] },
  ) => Promise<void>
  cancelQueue: () => void
  dismissQueue: () => void
  openDetail: () => void
  closeDetail: () => void
  openSelectionUi: () => void
  clearAwaitingPage: () => void
  /** 队列时间线点击：跳转画布或打开候选（详情 Modal / 灵动岛展开区） */
  focusQueueStep: (pageId: string, status: EpisodeQueuePageStatus) => void
  registerEditorBridge: (bridge: ComicEpisodeQueueEditorBridge) => void
  unregisterEditorBridge: (projectId: string) => void
}

const ComicEpisodeGenerateQueueContext = createContext<ComicEpisodeGenerateQueueContextValue | null>(
  null,
)

function episodeTitleFromJob(job: ComicEpisodeGenerateJob): string {
  const titled = job.pages.map((p) => p.title?.trim()).find((t) => t)
  return titled || '本话'
}

type ComicEpisodeGenerateQueueGlobalUiProps = {
  angular: boolean
  queue: EpisodeGenerateQueueState | null
  onFocusQueueStep: (pageId: string, status: EpisodeQueuePageStatus) => void
  onDismiss: () => void
}

/** 灵动岛活动注册；详情由全局 DynamicIslandActivityDetailModal 承载 */
function ComicEpisodeGenerateQueueGlobalUi({
  angular,
  queue,
  onFocusQueueStep,
  onDismiss,
}: ComicEpisodeGenerateQueueGlobalUiProps) {
  const { openActivityDetail } = useDynamicIsland()

  const handleOpenDetail = useCallback(() => {
    openActivityDetail('comic-episode-queue')
  }, [openActivityDetail])

  const handleQueueStepClick = useCallback(
    (pageId: string, status: EpisodeQueuePageStatus) => {
      onFocusQueueStep(pageId, status)
    },
    [onFocusQueueStep],
  )

  const activity = useMemo(() => {
    if (!queue) return null
    return mapComicQueueToActivity(queue, {
      onOpenDetail: handleOpenDetail,
      onClosePanel: onDismiss,
      renderDetail: () => (
        <DynamicIslandComicQueueDetailView
          queue={queue}
          angular={angular}
          onStepClick={handleQueueStepClick}
        />
      ),
    })
  }, [
    queue,
    angular,
    handleOpenDetail,
    handleQueueStepClick,
    onDismiss,
  ])

  useReportDynamicIslandActivity(activity)

  return null
}

export function ComicEpisodeGenerateQueueProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { isLoggedIn } = useAuthSession()
  const { showToast } = useToast()
  const canEpisodeJobCreate = useUserPerm(USER_PERMISSION.comicEpisodeJobCreate)
  const canEpisodeJobCancel = useUserPerm(USER_PERMISSION.comicEpisodeJobCancel)
  const canEpisodeJobRead = useUserPerm(USER_PERMISSION.comicEpisodeJobRead)
  const { clientUi } = usePlatformConfig()
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'

  const [queue, setQueue] = useState<EpisodeGenerateQueueState | null>(null)
  const [generatingEpisodeId, setGeneratingEpisodeId] = useState<string | null>(null)
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null)
  const [activeEpisodeId, setActiveEpisodeId] = useState<string | null>(null)
  const [awaitingPageId, setAwaitingPageId] = useState<string | null>(null)

  const queueCancelRef = useRef(false)
  const episodeJobRef = useRef<{ projectId: string; jobId: string } | null>(null)
  const editorBridgeRef = useRef<ComicEpisodeQueueEditorBridge | null>(null)
  const runGenerationRef = useRef(0)
  const queueSnapshotRef = useRef<EpisodeGenerateQueueState | null>(null)
  const hydrateInFlightRef = useRef(false)
  const { openActivityDetail, closeActivityDetail } = useDynamicIsland()

  const registerEditorBridge = useCallback((bridge: ComicEpisodeQueueEditorBridge) => {
    editorBridgeRef.current = bridge
  }, [])

  const unregisterEditorBridge = useCallback((projectId: string) => {
    if (editorBridgeRef.current?.projectId === projectId) {
      editorBridgeRef.current = null
    }
  }, [])

  const patchQueue = useCallback((next: EpisodeGenerateQueueState | null) => {
    queueSnapshotRef.current = next
    setQueue(next)
  }, [])

  const dismissQueue = useCallback(() => {
    runGenerationRef.current += 1
    patchQueue(null)
    closeActivityDetail()
    setAwaitingPageId(null)
    setGeneratingEpisodeId(null)
    setActiveProjectId(null)
    setActiveEpisodeId(null)
    episodeJobRef.current = null
  }, [patchQueue, closeActivityDetail])

  const cancelQueue = useCallback(() => {
    if (!canEpisodeJobCancel) {
      showToast({ message: '没有取消生成权限', variant: 'warning' })
      return
    }
    queueCancelRef.current = true
    const ref = episodeJobRef.current
    if (ref) {
      void cancelComicEpisodeGenerateJob(ref.projectId, ref.jobId).catch(() => {})
    }
    closeActivityDetail()
    setAwaitingPageId(null)
    setGeneratingEpisodeId(null)
    episodeJobRef.current = null
    setQueue((q) => {
      if (!q) return q
      const statuses = { ...q.statuses }
      for (const id of q.pageIds) {
        if (statuses[id] === 'pending' || statuses[id] === 'running') {
          statuses[id] = 'cancelled'
        }
      }
      const next = { ...q, cancelled: true, statuses, currentPageId: null }
      queueSnapshotRef.current = next
      return next
    })
  }, [canEpisodeJobCancel, showToast, closeActivityDetail])

  const openDetail = useCallback(() => {
    openActivityDetail('comic-episode-queue')
  }, [openActivityDetail])
  const closeDetail = useCallback(() => {
    closeActivityDetail()
  }, [closeActivityDetail])
  const clearAwaitingPage = useCallback(() => setAwaitingPageId(null), [])

  const openSelectionUi = useCallback(() => {
    if (!awaitingPageId || !activeProjectId || !activeEpisodeId) return
    if (!isValidComicProjectPublicId(activeProjectId)) return
    const bridge = editorBridgeRef.current
    if (bridge?.projectId === activeProjectId) {
      bridge.openPageCandidates(awaitingPageId)
      bridge.scrollToPage?.(awaitingPageId)
      return
    }
    navigate(`/comics/${encodeURIComponent(activeProjectId)}`, {
      state: {
        episodeId: activeEpisodeId,
        pageCandidatesPageId: awaitingPageId,
        openEpisodeQueueDetail: true,
      },
    })
  }, [awaitingPageId, activeProjectId, activeEpisodeId, navigate])

  const focusQueueStep = useCallback(
    (pageId: string, status: EpisodeQueuePageStatus) => {
      closeActivityDetail()
      if (status === 'awaiting_selection') {
        setAwaitingPageId(pageId)
        const bridge = editorBridgeRef.current
        if (bridge?.projectId === activeProjectId) {
          bridge.openPageCandidates(pageId)
          bridge.scrollToPage?.(pageId)
          return
        }
        if (activeProjectId && activeEpisodeId) {
          if (!isValidComicProjectPublicId(activeProjectId)) return
          navigate(`/comics/${encodeURIComponent(activeProjectId)}`, {
            state: {
              episodeId: activeEpisodeId,
              pageCandidatesPageId: pageId,
            },
          })
        }
        return
      }
      const bridge = editorBridgeRef.current
      if (bridge?.projectId === activeProjectId) {
        bridge.scrollToPage?.(pageId)
        return
      }
      if (activeProjectId && activeEpisodeId) {
        if (!isValidComicProjectPublicId(activeProjectId)) return
        navigate(`/comics/${encodeURIComponent(activeProjectId)}`, {
          state: { episodeId: activeEpisodeId, scrollToPageId: pageId },
        })
      }
    },
    [activeEpisodeId, activeProjectId, navigate, closeActivityDetail],
  )

  const waitForPageSelection = useCallback(
    async (projectId: string, episodeId: string, pageId: string) => {
      const bridge = editorBridgeRef.current
      if (bridge?.projectId === projectId) {
        await bridge.waitForPageSelection(pageId)
        return
      }
      while (!queueCancelRef.current) {
        await new Promise((r) => setTimeout(r, 2000))
        if (queueCancelRef.current) return
        try {
          const fresh = await getComicProject(projectId, clientUi)
          const awaiting = fresh.episodes
            .find((e) => e.id === episodeId)
            ?.pages.find((p) => p.pageImageStatus === 'pending_selection')
          setAwaitingPageId(awaiting?.id ?? null)
          const page = fresh.episodes
            .find((e) => e.id === episodeId)
            ?.pages.find((p) => p.id === pageId)
          if (page && page.pageImageStatus !== 'pending_selection') {
            editorBridgeRef.current?.applyProject(fresh, { save: false })
            setAwaitingPageId(null)
            return
          }
        } catch {
          /* 轮询失败则继续 */
        }
      }
    },
    [clientUi],
  )

  const runQueueJobAttachment = useCallback(
    async (
      project: ComicProject,
      episodeId: string,
      job: ComicEpisodeGenerateJob,
      runId: number,
    ) => {
      const ep = getActiveEpisode(project, episodeId)
      episodeJobRef.current = { projectId: project.id, jobId: job.publicId }
      const queue = buildQueueFromJob(job, ep.title)
      patchQueue(queue)
      const pageIds = job.pages.map((p) => p.pageId)
      const pageTitles = Object.fromEntries(
        job.pages.map((p) => [p.pageId, (p.title || p.pageId).trim() || p.pageId]),
      )

      try {
        const finished = await attachEpisodeGenerateJob({
          projectId: project.id,
          episodeId,
          episodeTitle: ep.title,
          pageIds,
          pageTitles,
          jobId: job.publicId,
          initialJob: job,
          clientUi,
          isCancelled: () => queueCancelRef.current || runGenerationRef.current !== runId,
          onJobStarted: (jobId) => {
            episodeJobRef.current = { projectId: project.id, jobId }
            notifyComicEpisodeQueueActivity()
          },
          onQueue: (q) => {
            if (runGenerationRef.current !== runId) return
            patchQueue(q)
            const awaitingId =
              q.pageIds.find((id) => q.statuses[id] === 'awaiting_selection') ?? null
            setAwaitingPageId(awaitingId)
          },
          onProject: (fresh) => {
            if (runGenerationRef.current !== runId) return
            editorBridgeRef.current?.applyProject(fresh, { save: false })
            const awaiting = fresh.episodes
              .find((e) => e.id === episodeId)
              ?.pages.find((p) => p.pageImageStatus === 'pending_selection')
            setAwaitingPageId(awaiting?.id ?? null)
          },
          waitForPageSelection: (pageId) => waitForPageSelection(project.id, episodeId, pageId),
        })

        if (runGenerationRef.current !== runId) return

        if (queueCancelRef.current || finished.cancelled) {
          showToast({ message: '已取消生成本话队列', variant: 'warning', autoCloseMs: 3500 })
        } else {
          const toast = formatEpisodeQueueCompletionToast(ep.title, queueSnapshotRef.current)
          if (toast) {
            showToast({ message: toast.message, variant: toast.variant, autoCloseMs: 5000 })
          } else if (finished.status === 'failed') {
            showToast({
              message: finished.errorMessage ?? '生成本话失败',
              variant: 'error',
            })
          }
        }
        const bridge = editorBridgeRef.current
        if (bridge?.projectId === project.id) {
          const latest = await getComicProject(project.id, clientUi)
          await bridge.persistProject(latest, true)
        }
      } catch (e) {
        if (runGenerationRef.current === runId) {
          const msg = isApiError(e) ? e.message : e instanceof Error ? e.message : '生成本话失败'
          showToast({ message: msg, variant: 'error' })
        }
      } finally {
        if (runGenerationRef.current === runId) {
          episodeJobRef.current = null
          setGeneratingEpisodeId(null)
        }
      }
    },
    [clientUi, patchQueue, showToast, waitForPageSelection],
  )

  const tryHydrateActiveEpisodeJobs = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!isLoggedIn) return false
      if (!canEpisodeJobRead) return false
      if (hydrateInFlightRef.current || queueSnapshotRef.current) return false

      hydrateInFlightRef.current = true
      let runId = 0
      try {
        const jobs = await listActiveComicEpisodeGenerateJobs()
        if (jobs.length === 0) return false

        const job = jobs[0]!
        runId = ++runGenerationRef.current
        queueCancelRef.current = false
        setActiveProjectId(job.projectPublicId)
        setActiveEpisodeId(job.episodeId)
        setGeneratingEpisodeId(job.episodeId)

        patchQueue(buildQueueFromJob(job, episodeTitleFromJob(job)))
        const awaiting = job.pages.find((p) => p.status === 'awaiting_selection')
        setAwaitingPageId(awaiting?.pageId ?? null)

        const project = await getComicProject(job.projectPublicId, clientUi)
        if (runGenerationRef.current !== runId) return false

        const ep = getActiveEpisode(project, job.episodeId)
        patchQueue(buildQueueFromJob(job, ep.title))

        if (!opts?.silent) {
          showToast({
            message: '检测到进行中的生图任务，已恢复进度',
            variant: 'info',
            autoCloseMs: 2800,
          })
        }
        await runQueueJobAttachment(project, job.episodeId, job, runId)
        return true
      } catch (e) {
        if (!opts?.silent) {
          const msg = isApiError(e) ? e.message : e instanceof Error ? e.message : '恢复生图任务失败'
          showToast({ message: msg, variant: 'error' })
        }
        console.error('[ComicEpisodeGenerateQueue] hydrate active jobs failed', e)
        if (runGenerationRef.current === runId) {
          patchQueue(null)
          setGeneratingEpisodeId(null)
          setActiveProjectId(null)
          setActiveEpisodeId(null)
          setAwaitingPageId(null)
        }
        return false
      } finally {
        hydrateInFlightRef.current = false
      }
    },
    [clientUi, isLoggedIn, canEpisodeJobRead, patchQueue, runQueueJobAttachment, showToast],
  )

  useEffect(() => {
    if (!isLoggedIn) return
    void tryHydrateActiveEpisodeJobs()

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (queueSnapshotRef.current) return
      void tryHydrateActiveEpisodeJobs({ silent: true })
    }
    document.addEventListener('visibilitychange', onVisible)

    const unsubscribeBc = subscribeComicEpisodeQueueActivity(() => {
      if (queueSnapshotRef.current) return
      void tryHydrateActiveEpisodeJobs({ silent: true })
    })

    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      unsubscribeBc()
    }
  }, [isLoggedIn, canEpisodeJobRead, tryHydrateActiveEpisodeJobs])

  const startEpisodeGenerate = useCallback(
    async (project: ComicProject, episodeId: string, options: { pageIds: string[] }) => {
      if (!canEpisodeJobCreate) {
        showToast({ message: '没有话级生成权限', variant: 'warning' })
        return
      }
      if (generatingEpisodeId) return
      const ep = getActiveEpisode(project, episodeId)
      const overflowPage = ep.pages.find((p) => pagePanelCapacity(p).overflow > 0)
      if (overflowPage) {
        showToast({
          message: `${overflowPage.title} 分镜 ${formatPagePanelCapacity(overflowPage)} 超出版式容量，请先调整分页`,
          variant: 'warning',
        })
        return
      }

      const pickedIds = options.pageIds.filter((id) => id.trim().length > 0)
      if (pickedIds.length === 0) {
        showToast({ message: '请至少选择一页参与生成', variant: 'warning' })
        return
      }

      let pages = resolveEpisodePagesForGenerate(ep, pickedIds)
      pages = pages.filter((p) => pageHasGeneratableScript(getPanelsOnPage(ep, p)))
      if (pages.length === 0) {
        showToast({ message: '所选页均不可生成（需填写画面描述）', variant: 'warning' })
        return
      }

      const settings = getEpisodeSettings(project, ep, clientUi)
      const settingsCheck = validateComicProjectSettings(settings, clientUi)
      if (!settingsCheck.ok) {
        showToast({ message: settingsCheck.message, variant: 'warning' })
        return
      }
      const { pageIds: jobPageIds } = buildEpisodeGenerateJobPages(project, ep, pages)
      if (jobPageIds.length === 0) {
        showToast({ message: '本话没有可生成的页（需填写画面描述）', variant: 'warning' })
        return
      }

      const runId = ++runGenerationRef.current
      queueCancelRef.current = false
      episodeJobRef.current = null
      closeActivityDetail()
      setAwaitingPageId(null)
      setActiveProjectId(project.id)
      setActiveEpisodeId(episodeId)

      const pageTitles = Object.fromEntries(pages.map((p) => [p.id, p.title]))
      const pageIds = pages.map((p) => p.id)
      const initialQueue = createEpisodeGenerateQueue(episodeId, ep.title, pageIds, pageTitles)
      patchQueue(initialQueue)
      setGeneratingEpisodeId(episodeId)

      try {
        const job = await runEpisodeGenerateJob({
          projectId: project.id,
          episodeId,
          episodeTitle: ep.title,
          pageIds: jobPageIds,
          pageTitles,
          clientUi,
          isCancelled: () => queueCancelRef.current || runGenerationRef.current !== runId,
          onJobStarted: (jobId) => {
            episodeJobRef.current = { projectId: project.id, jobId }
            notifyComicEpisodeQueueActivity()
          },
          onQueue: (q) => {
            if (runGenerationRef.current !== runId) return
            patchQueue(q)
            const awaitingId =
              q.pageIds.find((id) => q.statuses[id] === 'awaiting_selection') ?? null
            setAwaitingPageId(awaitingId)
          },
          onProject: (fresh) => {
            if (runGenerationRef.current !== runId) return
            editorBridgeRef.current?.applyProject(fresh, { save: false })
            const awaiting = fresh.episodes
              .find((e) => e.id === episodeId)
              ?.pages.find((p) => p.pageImageStatus === 'pending_selection')
            setAwaitingPageId(awaiting?.id ?? null)
          },
          waitForPageSelection: (pageId) => waitForPageSelection(project.id, episodeId, pageId),
        })

        if (runGenerationRef.current !== runId) return

        if (queueCancelRef.current || job.cancelled) {
          showToast({ message: '已取消生成本话队列', variant: 'warning', autoCloseMs: 3500 })
        } else {
          const toast = formatEpisodeQueueCompletionToast(ep.title, queueSnapshotRef.current)
          if (toast) {
            showToast({ message: toast.message, variant: toast.variant, autoCloseMs: 5000 })
          } else if (job.status === 'failed') {
            showToast({
              message: job.errorMessage ?? '生成本话失败',
              variant: 'error',
            })
          }
        }
        const bridge = editorBridgeRef.current
        if (bridge?.projectId === project.id) {
          const latest = await getComicProject(project.id, clientUi)
          await bridge.persistProject(latest, true)
        }
      } catch (e) {
        if (runGenerationRef.current === runId) {
          const msg = isApiError(e) ? e.message : e instanceof Error ? e.message : '生成本话失败'
          showToast({ message: msg, variant: 'error' })
        }
      } finally {
        if (runGenerationRef.current === runId) {
          episodeJobRef.current = null
          setGeneratingEpisodeId(null)
        }
      }
    },
    [canEpisodeJobCreate, clientUi, generatingEpisodeId, patchQueue, showToast],
  )

  const isGeneratingEpisode = useCallback(
    (episodeId: string) => generatingEpisodeId === episodeId,
    [generatingEpisodeId],
  )

  const value = useMemo(
    () => ({
      queue,
      generatingEpisodeId,
      activeProjectId,
      activeEpisodeId,
      awaitingPageId,
      isGeneratingEpisode,
      startEpisodeGenerate,
      cancelQueue,
      dismissQueue,
      openDetail,
      closeDetail,
      openSelectionUi,
      clearAwaitingPage,
      focusQueueStep,
      registerEditorBridge,
      unregisterEditorBridge,
    }),
    [
      queue,
      generatingEpisodeId,
      activeProjectId,
      activeEpisodeId,
      awaitingPageId,
      isGeneratingEpisode,
      startEpisodeGenerate,
      cancelQueue,
      dismissQueue,
      openDetail,
      closeDetail,
      openSelectionUi,
      clearAwaitingPage,
      focusQueueStep,
      registerEditorBridge,
      unregisterEditorBridge,
    ],
  )

  return (
    <ComicEpisodeGenerateQueueContext.Provider value={value}>
      {children}
      <ComicEpisodeGenerateQueueGlobalUi
        angular={angular}
        queue={queue}
        onFocusQueueStep={focusQueueStep}
        onDismiss={dismissQueue}
      />
    </ComicEpisodeGenerateQueueContext.Provider>
  )
}

export function useComicEpisodeGenerateQueue() {
  const ctx = useContext(ComicEpisodeGenerateQueueContext)
  if (!ctx) {
    throw new Error('useComicEpisodeGenerateQueue 须在 ComicEpisodeGenerateQueueProvider 内使用')
  }
  return ctx
}

/** 漫画编辑器挂载时注册，离页后队列与灵动岛仍常驻 */
export function useComicEpisodeQueueEditorBridge(bridge: ComicEpisodeQueueEditorBridge | null) {
  const { registerEditorBridge, unregisterEditorBridge } = useComicEpisodeGenerateQueue()

  useEffect(() => {
    if (!bridge) return
    registerEditorBridge(bridge)
    return () => unregisterEditorBridge(bridge.projectId)
  }, [bridge, registerEditorBridge, unregisterEditorBridge])
}
